// src/services/auditService/auditFsAnnualService.ts
import {BASE_AUDIT_API, authorizedFetch, fetchAPI, getHttpErrorMessage} from '@/services/api';
import {RequestPage, ResponsePage} from '@/services/page.interface';
import {AuditBuildCompanyFSTreeItems} from "@/services/auditService/auditCompanyFSCategoryService";

// VO: AuditFsAnnualStatusVO
export interface AuditFsAnnualStatusVO {
    id: number;
    companyId: number;
    annualId: number;
    fsStatus: number;
    isActive: number;
}

// VO: AuditAnnualFsPageVO (for page list items)
export interface AuditAnnualFsPageVO {
    companyId: number;
    companyName: string;
    companyChineseName: string;
    annualId: number;
    yearOfAssessment: string;
    periodTo: string;
    fsStatus?: number;
    flow?: string;
}

export interface AuditAnnualFs {
    id: number;
    companyId: number;
    annualId: number;
    periodFrom: string;
    periodTo: string;
    lastYearPeriodFrom: string | null;
    lastYearPeriodTo: string | null;
    balanceSheet: AuditFsItem;
    profitLoss: AuditFsItem;
    firstYearAudit: boolean | false;
}

export interface AuditFsItem {
    fsId: number | null;
    level: number | null;
    name: string | null;
    code: string | null;
    entry: string | null;
    scheduleName: string | null;
    scheduleCode: string | null;
    openingAmount: number | null;
    lastYearAudited: number | null;
    lastYearChangeAudited: number | null;
    /** Per-YOA amounts for MULTI FS (Phase 3). Key "" = legacy SINGLE. */
    amountsByYearOfAssessment?: Record<string, number> | null;
    adjustByPaper?: boolean;
    children?: AuditFsItem[];
}

export interface AuditAnnualFsRequest {
    id: number;
    companyId: number;
    annualId: number;
    balanceSheetLines: Array<{ fsId: number; amount: number; lastYearChangeAudited?: number }>;
    profitLossLines: Array<{
        fsId: number;
        amount: number;
        lastYearChangeAudited?: number;
        /** Sub-period YOA for MULTI FS; omit/blank = SINGLE legacy */
        yearOfAssessment?: string | null;
    }>;
    /** GL Submit 时为 true：将汇总金额写入尚无数据的 audit 底稿 commonRow */
    syncWpFromGl?: boolean;
    /** FsDetail Update/Submit：刷新 BS/PL 后保留 wp_status / wp_issue_remark */
    preserveWpFields?: boolean;
}

export interface AuditBalanceSheetItem {
    id: number;
    annualId: number;
    companyId: number;
    lines: AuditBuildCompanyFSTreeItems[];
    notes: string;
    displayOrder: number;
    isActive: number;
}

export interface AuditProfitLossItem {
    id: number;
    annualId: number;
    companyId: number;
    lines: AuditBuildCompanyFSTreeItems[];
    notes: string;
    displayOrder: number;
    isActive: number;
}

const base = `${BASE_AUDIT_API}/fs-status`;

// POST /fs-status/queryPage?fsStatus=...
export interface RequestFsPage extends RequestPage {
    companyName?: string;
    yearOfAssessment?: string;
}

export async function queryPage(fsStatus: number, pageRequest: RequestFsPage) {
    return fetchAPI<ResponsePage<AuditAnnualFsPageVO>>(
        `${base}/queryPage?fsStatus=${encodeURIComponent(String(fsStatus))}`, {
            method: 'POST',
            body: JSON.stringify(pageRequest),
        },
    );
}

// GET /fs-status/get?companyId=...&annualId=...&importToken=...
export async function get(companyId: number, annualId: number, importToken?: string | null) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    if (importToken) {
        params.append('importToken', importToken);
    }
    return fetchAPI<AuditAnnualFs>(`${base}/get?${params.toString()}`);
}

// POST /fs-status/createFs
export async function changeFs(body: Partial<AuditAnnualFsRequest>) {
    return fetchAPI<AuditAnnualFs>(`${base}/changeFs`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// POST /fs-status/processAliases?companyId=...&annualId=...&importToken=...
export async function processAliases(companyId: number, annualId: number, importToken: string): Promise<AliasLockedNode[]> {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        importToken,
    });
    return fetchAPI<AliasLockedNode[]>(`${base}/processAliases?${params.toString()}`, {
        method: 'POST',
    });
}

// POST /fs-status/changeStatus?companyId=...&annualId=...&targetStatus=...
export async function changeStatus(companyId: number, annualId: number, targetStatus: number) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        targetStatus: String(targetStatus),
    });
    return fetchAPI<boolean>(`${base}/changeStatus?${params.toString()}`, {
        method: 'POST',
    });
}

export interface WpStatusUpdateRequest {
    companyId: number;
    annualId: number;
    fsId: number;
    wpStatus: number | null;
    wpIssueRemark?: string | null;
}

export async function updateWpStatus(body: WpStatusUpdateRequest) {
    return fetchAPI<{ fsId: number; wpStatus?: number | null; wpIssueRemark?: string | null }>(
        `${base}/wp-status`,
        {method: 'POST', body: JSON.stringify(body)},
    );
}

export interface ScheduleNotesVO {
    fsId?: number;
    notes?: string | null;
    wpStatus?: number | null;
    wpIssueRemark?: string | null;
}

export interface ScheduleNotesUpdateRequest {
    companyId: number;
    annualId: number;
    fsId: number;
    notes?: string | null;
}

export async function getScheduleNotes(companyId: number, annualId: number, fsId: number) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        fsId: String(fsId),
    });
    return fetchAPI<ScheduleNotesVO>(`${base}/notes?${params.toString()}`, {method: 'GET'});
}

export async function updateScheduleNotes(body: ScheduleNotesUpdateRequest) {
    return fetchAPI<ScheduleNotesVO>(`${base}/notes`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// GET /fs-status/exists?companyId=...&annualId=...
export async function exists(companyId: number, annualId: number) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    return fetchAPI<boolean>(`${base}/exists?${params.toString()}`);
}

// ContentSheetData — parsed from "Content" sheet
export interface ContentSheetData {
    companyName: string | null;
    companyChineseName: string | null;
    br: string | null;
    inCorpDate: string | null;
    auditCertificateId: number | null;
    periodFrom: string | null;
    periodTo: string | null;
    lastYearPeriodFrom: string | null;
    lastYearPeriodTo: string | null;
    changeOfFinancialYearEnd: number | null;
    dateOfBoardResolution: string | null;
    reasonToChange: string | null;
    cessationDuringThisPeriod: number | null;
    /** Importer sets true when B6.2 is multiyear (Year 1/2/3). */
    multiYearPl?: boolean | null;
    limitByGuarantee: number | null;
    liabilitiesAmount: number | null;
    liabilitiesCurrency: string | null;
    otherNameThanCompany: string | null;
    section88: number | null;
    taxFileNo: string | null;
    isChangeName: number | null;
    oldCompanyName: string | null;
    changeEffectiveDate: string | null;
    formerAuditor: string | null;
    addressOfFormerAuditor: string | null;
    professionalClearance: string | null;
    qualifyOpinion: number | null;
    firstAudit: number | null;
    presentationCurrency: string | null;
    exchangeRate: number | null;
    functionalCurrency: string | null;
    dividendPaid: number | null;
    dividendCurrency: string | null;
    amountPerEachShare: number | null;
    totalDividendAmount: number | null;
    dividendPayableBf?: number | null;
    dividendDeclared?: number | null;
    dividendPaidAmount?: number | null;
    twoTierTaxThisYear: number | null;
    twoTierTaxLastYear: number | null;
    taxLossBF: number | null;
    referrer: string | null;
    inCharge: string | null;
}

export interface MatchedCompany {
    companyId: number;
    companyName: string;
    companyChineseName: string | null;
    inCorpDate: string | null;
    br: string | null;
}

// ExcelImportResult
export interface ExcelImportResult {
    // Phase 1
    fsData: AuditAnnualFs;
    bsMatchedCount: number;
    bsSkippedCount: number;
    plMatchedCount: number;
    plSkippedCount: number;
    unmatchedCodes: string[];
    // Phase 2
    contentData: ContentSheetData | null;
    matchedCompany: MatchedCompany | null;
    companyMatched: boolean;
    companyNameUpdated: boolean;
    // Phase 3
    importToken: string | null;
    // Phase 4: Excel 改名涉及的锁定节点（不可改名），用于前端警告提示
    aliasLockedNodes?: AliasLockedNode[];
}

// 不可改名的节点（level<=2 或 alias_locked=1）
export interface AliasLockedNode {
    code: string;
    name: string;
}

/** importExcel: assessable year already exists for company */
export const FS_IMPORT_ASSESSABLE_YEAR_EXISTS = 1408;

// GET /fs-status/flowByAnnualIds?annualIds=...
export async function getFlowByAnnualIds(annualIds: number[]): Promise<Record<number, string>> {
    const params = new URLSearchParams();
    annualIds.forEach(id => params.append('annualIds', String(id)));
    return fetchAPI<Record<number, string>>(`${base}/flowByAnnualIds?${params.toString()}`);
}

// POST /fs-status/initFsStatus?companyId=&annualId=&flowType=
export async function initFsStatus(companyId: number, annualId: number, flowType: number): Promise<void> {
    await fetchAPI<void>(`${base}/initFsStatus?companyId=${companyId}&annualId=${annualId}&flowType=${flowType}`, {
        method: 'POST',
    });
}

// POST /fs-status/importExcel (multipart/form-data)
export async function importExcel(file: File, companyId?: number | null): Promise<ExcelImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (companyId != null) formData.append('companyId', String(companyId));

    // Must use authorizedFetch so path is ciphered when payload cipher is on.
    // FormData body stays multipart (backend CryptoPayloadFilter skips multipart).
    const res = await authorizedFetch(`${base}/importExcel`, {
        method: 'POST',
        body: formData,
    });
    if (!res.ok) throw new Error(await getHttpErrorMessage(res, 'Upload failed'));
    const result = await res.json();
    if (result.code !== 0) {
        const err = new Error(result.msg || 'Import failed') as Error & { code?: number };
        err.code = result.code;
        throw err;
    }
    return result.data;
}

export type FsExcelTemplateVariant = 'single' | 'multi';

function tryParseFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
    if (!contentDisposition) return undefined;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    const raw = match?.[1] || match?.[2];
    if (!raw) return undefined;
    try {
        return decodeURIComponent(raw.replace(/\+/g, '%20'));
    } catch {
        return raw.replace(/\+/g, ' ');
    }
}

export function triggerFsBlobDownload(blob: Blob, filename: string) {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    window.setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(blobUrl);
    }, 1500);
}

/**
 * Download blank AFS Excel template for FS create/import.
 * GET /fs-status/excel-template?variant=single|multi
 */
export async function downloadFsExcelTemplate(
    variant: FsExcelTemplateVariant = 'single',
): Promise<{ blob: Blob; filename?: string }> {
    // Must use authorizedFetch so path is ciphered when payload cipher is on.
    const res = await authorizedFetch(
        `${base}/excel-template?variant=${encodeURIComponent(variant)}`,
        {method: 'GET'},
    );
    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Template download failed'));
    }
    // Gateway crypto errors often return HTTP 200 + JSON Result (e.g. code 4605).
    const ct = (res.headers.get('Content-Type') || '').toLowerCase();
    if (ct.includes('json')) {
        throw new Error(await getHttpErrorMessage(res, 'Template download failed'));
    }
    const blob = await res.blob();
    const filename = tryParseFilenameFromContentDisposition(res.headers.get('Content-Disposition'));
    return { blob, filename };
}
